/**
 * A dialog in the middle of the screen.
 *
 * The third shape this application needs, after the edge sheet and the command palette, and it
 * exists because neither of those fits a chooser: a sheet arrives from an edge and belongs to the
 * editor's layout, and the palette is a list with a filter. Picking a template is a grid that
 * wants the room a centred panel gives it.
 *
 * Radix's Dialog again, for the reasons `Sheet` sets out — a focus trap, Escape, a click outside,
 * the rest of the page hidden from assistive technology, and the body held still behind it. The
 * caret's arrival and departure go through `useDialogFocus`, so all three dialogs answer the
 * question "where does focus go when this closes" in one place rather than three.
 *
 * On a phone it stops being centred and becomes the whole screen. A 375px window has no room for
 * a panel with a margin around it, and a chooser scaled down to fit inside one is a chooser
 * nobody can read.
 */
import * as Dialog from '@radix-ui/react-dialog';

import { useDialogFocus } from './dialogFocus';
import { IconClose } from './icons';
import { IconButton } from './controls';

/**
 * How much room the panel takes on a screen that has room to give.
 *
 * `wide` is the create dialog, which is a grid of pictures. `narrow` is a chooser — two or three
 * things and a sentence — where the same 56rem would put a pair of buttons in the middle of an
 * empty field. Below the `sm` breakpoint neither applies: a 375px window has no room for a panel
 * with a margin around it, so both become the whole screen.
 *
 * `wide` also has a height of its own rather than its content's. Its tabs hold very different
 * amounts — a gallery of sixty layouts, a shelf of five templates, a text field — and a panel that
 * resized to each would jump under the pointer every time a tab was chosen.
 */
export type ModalSize = 'narrow' | 'wide';

const WIDTH: Readonly<Record<ModalSize, string>> = {
  narrow: 'sm:w-[min(26rem,calc(100vw-3rem))]',
  wide: 'sm:h-[min(46rem,85dvh)] sm:w-[min(56rem,calc(100vw-3rem))]',
};

export interface ModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly size?: ModalSize;
  /** Shown under the title, and the dialog's accessible description. */
  readonly description?: string;
  /** What should hold focus on arrival. Absent lets Radix take the close button. */
  readonly autoFocusSelector?: string;
  readonly children: React.ReactNode;
}

export function Modal({
  open,
  onOpenChange,
  title,
  size = 'wide',
  description,
  autoFocusSelector,
  children,
}: ModalProps): React.JSX.Element {
  const focus = useDialogFocus({ autoFocusSelector });

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="animate-fade-in fixed inset-0 z-40 bg-black/35 backdrop-blur-[2px]" />
        <Dialog.Content
          onOpenAutoFocus={focus.onOpenAutoFocus}
          onCloseAutoFocus={focus.onCloseAutoFocus}
          className={`border-edge bg-surface text-ink animate-pop-in fixed inset-0 z-50 flex flex-col overflow-hidden border shadow-2xl outline-none sm:inset-auto sm:top-1/2 sm:left-1/2 sm:max-h-[85dvh] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl ${WIDTH[size]}`}
        >
          <div className="border-edge flex shrink-0 items-start justify-between gap-3 border-b px-4 py-3 sm:px-5">
            <div className="min-w-0">
              <Dialog.Title className="text-[0.9375rem] font-semibold">{title}</Dialog.Title>
              {description === undefined ? (
                <Dialog.Description className="sr-only">{title}</Dialog.Description>
              ) : (
                <Dialog.Description className="text-ink-muted mt-0.5 text-xs">
                  {description}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close asChild>
              <IconButton label="Close" icon={<IconClose size={18} />} size="sm" />
            </Dialog.Close>
          </div>

          {/*
            A column, so a child that wants to keep a footer in view can take the height with
            `flex-1 min-h-0` and scroll inside itself. Anything that does not ask keeps its own
            height and scrolls here, as before.
          */}
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
            {children}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
